// Parte diario de huéspedes (script 930): quién durmió en el hotel una noche,
// con los datos del registro que piden Turismo / Policía (y la TCT en
// Galápagos). Se exporta a CSV para enviarlo a la autoridad.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, fieldCls, hotelRpc, QK, fmtFecha, useSort, SortTh } from './shared';

interface FilaParte {
  habitacion: string | null; reserva: string; llegada: string; salida: string; nombres: string; tipo_documento: string; documento: string;
  nacionalidad: string | null; procedencia: string | null; fecha_nacimiento: string | null; edad: number | null;
  residencia: string | null; tct: string | null; motivo_viaje: string | null; responsable: string | null; es_titular: boolean;
}
interface ParteResp { fecha: string; huespedes: FilaParte[] }

const COLS: [keyof FilaParte, string][] = [
  ['habitacion', 'Habitación'], ['nombres', 'Nombres'], ['tipo_documento', 'Tipo doc.'], ['documento', 'Documento'],
  ['nacionalidad', 'Nacionalidad'], ['procedencia', 'Procedencia'], ['edad', 'Edad'], ['residencia', 'Residencia'],
  ['tct', 'TCT'], ['motivo_viaje', 'Motivo'], ['llegada', 'Llegada'], ['salida', 'Salida'],
];
const getVal = (f: FilaParte, k: string) => {
  const v = f[k as keyof FilaParte];
  return typeof v === 'number' ? v : v == null ? null : String(v);
};

export function ParteHuespedes() {
  const [fecha, setFecha] = useState('');
  const { data, isLoading } = useQuery<ParteResp>({
    queryKey: [QK, 'parte', fecha],
    queryFn: () => hotelRpc<ParteResp>('parte_huespedes', { _fecha: fecha || null }),
  });
  const filas = useMemo(() => data?.huespedes ?? [], [data]);
  const sort = useSort(filas, getVal, 'habitacion');
  const menores = filas.filter((f) => f.edad != null && f.edad < 18).length;
  const habitaciones = new Set(filas.map((f) => f.habitacion)).size;

  const exportar = () => {
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = [...COLS.map(([, l]) => l), 'Adulto responsable', 'Titular'];
    const body = sort.sorted.map((f) => [...COLS.map(([k]) => f[k]), f.responsable, f.es_titular ? 'Sí' : 'No'].map(esc).join(';'));
    const blob = new Blob(['﻿' + [head.map(esc).join(';'), ...body].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `parte-huespedes-${data?.fecha ?? 'hoy'}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold"><ClipboardList className="h-5 w-5" /> Parte de huéspedes</h1>
        <div className="flex items-end gap-2">
          <Campo label="Noche del"><input type="date" className={fieldCls} value={fecha || data?.fecha || ''} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <Button variant="outline" disabled={filas.length === 0} onClick={exportar}><Download className="mr-1 h-4 w-4" /> Exportar CSV</Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Huéspedes registrados que pasaron la noche del {fmtFecha(data?.fecha)}: {filas.length} personas en {habitaciones} habitación(es)
        {menores > 0 && <> · <strong>{menores} menor(es) de edad</strong></>}.
      </p>
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-3">N°</th>
              {COLS.map(([k, l]) => <SortTh key={k} col={k} sort={sort}>{l}</SortTh>)}
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS.length + 1} className="py-8 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && filas.length === 0 && <tr><td colSpan={COLS.length + 1} className="py-8 text-center text-muted-foreground">Sin huéspedes registrados esa noche.</td></tr>}
              {sort.sorted.map((f, i) => (
                <tr key={`${f.reserva}-${f.documento}-${i}`} className="border-b last:border-0">
                  <td className="py-1.5 pl-3 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3 font-mono">{f.habitacion ?? '—'}</td>
                  <td className="py-1.5 pr-3">{f.nombres}{f.es_titular && <span className="ml-1 text-xs text-muted-foreground">(titular)</span>}
                    {f.responsable && <div className="text-xs text-amber-700 dark:text-amber-400">Responsable: {f.responsable}</div>}</td>
                  <td className="py-1.5 pr-3 capitalize">{f.tipo_documento}</td>
                  <td className="py-1.5 pr-3 font-mono">{f.documento}</td>
                  <td className="py-1.5 pr-3">{f.nacionalidad ?? '—'}</td>
                  <td className="py-1.5 pr-3">{f.procedencia ?? '—'}</td>
                  <td className={`py-1.5 pr-3 tabular-nums ${f.edad != null && f.edad < 18 ? 'font-semibold text-amber-700 dark:text-amber-400' : ''}`}>{f.edad ?? '—'}</td>
                  <td className="py-1.5 pr-3 capitalize">{f.residencia ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono">{f.tct ?? '—'}</td>
                  <td className="py-1.5 pr-3 capitalize">{f.motivo_viaje ?? '—'}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{fmtFecha(f.llegada)}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{fmtFecha(f.salida)}</td>
                </tr>
              ))}
            </tbody>
            {filas.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={COLS.length + 1} className="py-2 pl-3">Total: {filas.length} huéspedes · {habitaciones} habitaciones</td></tr></tfoot>}
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
